import { useState, useEffect } from "react";
import dynamic from "next/dynamic";

const Lottie = dynamic(() => import("lottie-react"), { ssr: false });

// 音频均衡器 Lottie 动画缓存 (模块级缓存，组件间共享)
let cachedAudioEqualizerAnimation: any = null;
const loadPromiseRef: Promise<any> | null = null;

/**
 * 预加载音频均衡器动画
 * 可在应用启动时调用，避免首次渲染时加载
 */
export const preloadAudioEqualizerAnimation = (): Promise<any> => {
  if (cachedAudioEqualizerAnimation) {
    return Promise.resolve(cachedAudioEqualizerAnimation);
  }
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }
  // 复用正在进行的加载请求，避免重复 fetch
  const existingPromise = (preloadAudioEqualizerAnimation as any).__promise;
  if (existingPromise) {
    return existingPromise;
  }
  (preloadAudioEqualizerAnimation as any).__promise = fetch(
    "/lottie/audio-equalizer.json"
  )
    .then((res) => res.json())
    .then((data) => {
      cachedAudioEqualizerAnimation = data;
      (preloadAudioEqualizerAnimation as any).__promise = null;
      return data;
    })
    .catch((error) => {
      console.error("Failed to load audio equalizer animation:", error);
      (preloadAudioEqualizerAnimation as any).__promise = null;
      return null;
    });
  return (preloadAudioEqualizerAnimation as any).__promise;
};

export interface AudioEqualizerIconProps {
  size?: number;
  className?: string;
}

/**
 * 音频均衡器动画图标组件
 * 用于展示播放状态的动态音频条动画
 *
 * 优化策略:
 * 1. 模块级缓存避免重复加载
 * 2. 预加载函数可在应用启动时调用
 * 3. 始终渲染内容 (静态 SVG 或 Lottie),避免白屏
 */
export function AudioEqualizerIcon({
  size = 24,
  className,
}: AudioEqualizerIconProps) {
  const [animation, setAnimation] = useState<any>(
    cachedAudioEqualizerAnimation
  );

  useEffect(() => {
    // 如果已有缓存，直接使用
    if (cachedAudioEqualizerAnimation) {
      return;
    }
    // 否则加载动画
    preloadAudioEqualizerAnimation().then(setAnimation);
  }, []);

  // 动画加载完成后，渲染 Lottie
  if (animation) {
    return (
      <Lottie
        animationData={animation}
        loop={true}
        autoplay={true}
        style={{ width: size, height: size }}
        className={className}
      />
    );
  }

  // 动画加载过程中显示静态 SVG 作为 fallback (避免白屏)
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <rect x="3.5" y="4" width="2" height="15" fill="var(--theme-primary)" />
      <rect x="8.5" y="8" width="2" height="8" fill="var(--theme-primary)" />
      <rect x="13.5" y="3" width="2" height="18" fill="var(--theme-primary)" />
      <rect x="18.5" y="6" width="2" height="12" fill="var(--theme-primary)" />
    </svg>
  );
}
